import type { FieldData, FormInstance } from 'rc-field-form/es/interface';

import {
  AvailableOn,
  WeekDays,
} from 'app/components/commons/Experience/Availability/utils';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import type {
  PackageInventory,
  PackagePrice,
  Planning,
  RoomInventory,
} from 'app/typings/Extranet/inventory';
import { ChannelManager } from 'app/typings/channelManagers';
import type { ExperienceCategory } from 'app/typings/experiences';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import type { ExtranetExperience, ExtranetPackage } from 'app/typings/packages';
import { formatDateWithoutHour, formatToLocalDateTime } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import {
  addDays,
  eachDayOfInterval,
  format,
  isAfter,
  isBefore,
  isSunday,
  nextSunday,
  subDays,
} from 'app/utils/dayjs';
import { calculateDiscount, computeCrossRate } from 'app/utils/price';

import type {
  DuplicationModeData,
  FormPackage,
  FormPackagePrice,
  FormPlanning,
  FormType,
  TimePeriod,
} from './type';
import { DuplicationMode, OpeningErrors, OpeningsErrorMessage } from './type';

export const MINIMAL_DISCOUNT_DAY_PACKAGE = 0;
export const MINIMAL_DISCOUNT_STAYCATION = 18;
export const MAXIMUM_DISCOUNT = 70;
export const NUMBER_OF_DAYS_RANGE = 16;
export const NUMBER_OF_WEEKDAYS = 7;

const DEFAULT_PLANNING: FormPlanning = {
  date: '',
  stock: 0,
  booked: 0,
  autoStock: false,
  autoBar: false,
  closed: false,
  packagePrices: {},
  errors: [],
};

const DEFAULT_PACKAGE_PRICE: FormPackagePrice = {
  autoDiscount: false,
  closed: false,
  isForced: false,
  lastMinuteAutoDiscount: false,
  published: false,
  updatedAt: formatToLocalDateTime(new Date()),
  errors: [],
};

const getDay = (dayOfWeek: number) =>
  Object.values(WeekDays).find(
    (_, index) =>
      (index + 1) % NUMBER_OF_WEEKDAYS === dayOfWeek % NUMBER_OF_WEEKDAYS
  );

export const checkDaysRestrictionsForSingleNight = (
  date: Date,
  isDayPackage: boolean,
  availableDays: WeekDays[],
  availableDayLimits: AvailableOn[],
  availableDates: string[]
) => {
  const noDayRestriction =
    availableDays.length === 0 && availableDates.length === 0;
  const currentDay = date.getDay();

  const checkinDate = formatDateWithoutHour(date);
  const checkoutDate = formatDateWithoutHour(
    addDays(date, isDayPackage ? 0 : 1)
  );

  const checkinDay = getDay(currentDay);
  const checkoutDay = getDay(currentDay + (isDayPackage ? 0 : 1));

  const isCheckinDayValid =
    availableDayLimits.includes(AvailableOn.CHECK_IN) &&
    (noDayRestriction ||
      (checkinDay &&
        (availableDays?.includes(checkinDay) ||
          availableDates?.includes(checkinDate))));

  const isCheckoutDayValid =
    availableDayLimits.includes(AvailableOn.CHECK_OUT) &&
    (noDayRestriction ||
      (checkoutDay && availableDays?.includes(checkoutDay)) ||
      availableDates?.includes(checkoutDate));

  return isCheckinDayValid || isCheckoutDayValid;
};

export const isExperienceOptional = (
  packageId: number,
  exp: ExtranetExperience
) =>
  exp.included[packageId] &&
  exp.price &&
  exp.price > 0 &&
  (exp.closingPeriods?.length ||
    (exp.availableDays?.length && exp.availableDays.length < 7) ||
    exp.availableDates?.length);

export const computeCrossedPriceDescription: (
  packageId: number,
  experiences: ExtranetExperience[],
  currencyFormatter: (price?: number) => string,
  isDayPackage: boolean,
  experiencesCategories?: ExperienceCategory[]
) => string = (
  packageId,
  experiences,
  currencyFormatter,
  isDayPackage,
  experiencesCategories
) => {
  if (!experiences) {
    return '';
  }

  const includedExperiences = experiences.filter(
    (experience) => experience.included[packageId] && experience.hotelId
  );

  const crossPriceDescription = includedExperiences.reduce((acc, exp) => {
    const parsedPrice = exp.price || 0;
    const isOptional = isExperienceOptional(packageId, exp) ? '*' : '';
    const descriptionSuffix = exp.priceDescription
      ? ' ' + exp.priceDescription
      : '';

    const expCat = experiencesCategories?.find(
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      (expCat) => expCat.id === exp.categoryId
    );

    const priceInfo = isDayPackage
      ? ''
      : ` (${currencyFormatter(parsedPrice)}${descriptionSuffix})`;

    return `${acc}${acc ? ' + ' : ''}${
      expCat?.name ?? ''
    }${isOptional}${priceInfo}`;
  }, '');

  return crossPriceDescription;
};

export const getDatesToFill = (
  day: Date,
  mode: DuplicationModeData,
  timePeriod: TimePeriod
): Interval => {
  switch (mode.mode) {
    case DuplicationMode.END_OF_WEEK:
      return { start: day, end: nextSunday(subDays(day, 1)) };

    case DuplicationMode.END_OF_NEXT_WEEK:
      if (!isSunday(day)) {
        return { start: day, end: nextSunday(addDays(day, 6)) };
      }

      return { start: day, end: nextSunday(day) };

    case DuplicationMode.END_OF_TABLE:
      return { start: day, end: timePeriod.endDate };
    case DuplicationMode.CUSTOM:
      return { start: day, end: addDays(mode.customDay, 1) };
    default:
      throw new Error('Unsupported Duplication mode');
  }
};

const renderChannelManagerHeader = (
  channelManager?: string,
  iconPath?: string,
  classPrefix?: string,
  inventory?: FormType
) => {
  if (!inventory) {
    return undefined;
  }

  const lastUpdate = () => {
    return Object.values(inventory.rooms)
      .flatMap((room) => Object.values(room.plannings))
      .flatMap((planning) => Object.values(planning.packagePrices))
      .flatMap((packagePrice) => packagePrice.updatedAt)
      .sort((a, b) => {
        return isBefore(new Date(a), new Date(b)) ? 1 : -1;
      })[0];
  };

  return (
    <div className="header">
      {!!channelManager && (
        <div className={`${classPrefix}-header`}>
          <div className={`${classPrefix}-header__thumbnail`}>
            {iconPath && <PictureComponent pictureId={iconPath} />}
          </div>
          {channelManager}
        </div>
      )}
      <div className="header__updated">
        {lastUpdate()
          ? `Last update ${format(new Date(lastUpdate()), 'DD/MM/YYYY HH:mm')}`
          : ''}
      </div>
    </div>
  );
};

export const renderHeader = (channelManager?: string, inventory?: FormType) => {
  if (channelManager) {
    switch (channelManager) {
      case ChannelManager.SITEMINDER: {
        return renderChannelManagerHeader(
          `Siteminder`,
          'static/icons/siteminder.png',
          'siteminder',
          inventory
        );
      }

      case ChannelManager.D_EDGE: {
        return renderChannelManagerHeader(
          `D-Edge`,
          'static/icons/d-edge.png',
          'dedge',
          inventory
        );
      }

      case ChannelManager.RESERVIT: {
        return renderChannelManagerHeader(
          `Reservit`,
          'static/icons/reservit.png',
          'reservit',
          inventory
        );
      }

      case ChannelManager.CUBILIS: {
        return renderChannelManagerHeader(
          'Cubilis',
          'static/icons/cubilis.png',
          'cubilis',
          inventory
        );
      }

      case ChannelManager.TRAVELCLICK: {
        return renderChannelManagerHeader(
          'Travelclick',
          'static/icons/travelclick.png',
          'travelclick',
          inventory
        );
      }

      case ChannelManager.ROOMCLOUD: {
        return renderChannelManagerHeader(
          'Roomcloud',
          'static/icons/roomcloud.png',
          'roomcloud',
          inventory
        );
      }

      case ChannelManager.MEWS: {
        return renderChannelManagerHeader(
          'Mews',
          'static/icons/mews.png',
          'mews',
          inventory
        );
      }

      default: {
        return renderChannelManagerHeader();
      }
    }
  }

  return renderChannelManagerHeader();
};

export const getTooltipDescription = (
  name: string,
  experienceDescription?: string,
  experienceValue?: string,
  isDayPackage?: boolean
) => {
  switch (name) {
    case 'bar':
      return !isDayPackage ? (
        <>
          BAR is the Best Available Rate (Non Amendable/Non Refundable) on your
          website on each day, for each room category
        </>
      ) : (
        <>Public price /2 pers. = {experienceDescription}</>
      );

    case 'discountPrice':
      return !isDayPackage ? (
        <>Crossed rate = BAR + {experienceDescription}</>
      ) : (
        <> Public price /2 pers. - Staycation discount</>
      );

    case 'discount':
      return !isDayPackage ? (
        <>
          The minimum discount rate is 18%, on the total value of the package
          <br />
          Staycation rate = BAR + Package ({experienceValue}) - Staycation
          discount
        </>
      ) : undefined;

    default:
      return undefined;
  }
};

const getCrossRate = (
  bar: number,
  date: string,
  packageId: number,
  isDayPackage: boolean,
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[]
) => {
  return computeCrossRate(
    packageId,
    getExperiencesForPackage(packageId),
    date,
    isDayPackage,
    bar
  );
};

const getOpeningErrors = (
  bar: number,
  stock: number,
  booked: number,
  roomCount: number,
  closed: boolean,
  hasAtLeastOneDayPackage: boolean
) => {
  const errors: OpeningErrors[] = [];

  if (!bar || bar <= 0) {
    errors.push(OpeningErrors.MISSING_BAR);
  }

  if (!stock || stock === 0) {
    errors.push(OpeningErrors.MISSING_STOCK);
  }

  if (!hasAtLeastOneDayPackage && stock + booked > roomCount) {
    errors.push(OpeningErrors.WRONG_STOCK);
  }

  if (closed) {
    errors.push(OpeningErrors.CLOSED);
  }

  return errors;
};

const getPackagePriceErrors = (
  price: number,
  discountPrice: number,
  isDayPackage: boolean,
  isForced: boolean,
  published: boolean,
  date: string,
  minimumDiscountOverrideDates: Date[],
  minimumDiscountOverride?: number
) => {
  const errors: OpeningErrors[] = [];

  if (
    isDiscountNotEnough(
      discountPrice,
      price,
      isDayPackage,
      date,
      minimumDiscountOverrideDates,
      minimumDiscountOverride
    )
  ) {
    errors.push(OpeningErrors.EXCEED_RATE);
  }

  if (!discountPrice) {
    errors.push(OpeningErrors.MISSING_DISCOUNT_PRICE);
  }

  if (isDiscountTooMuch(discountPrice, price)) {
    errors.push(OpeningErrors.UNDER_MINIMUM_PRICE);
  }

  if (discountPrice !== undefined && discountPrice > price) {
    errors.push(OpeningErrors.NO_DISCOUNT);
  }

  if (isForced && !published) {
    errors.push(OpeningErrors.REJECTED_BY_AM);
  }

  return errors;
};

const completePackagePrice = (
  planning: Planning,
  packagePrice: PackagePrice,
  packages: ExtranetPackage[],
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[]
) => {
  const pkg = packages.find((p) => p.id === packagePrice.packageId);
  const isDayPackage = pkg?.dayPackage || false;

  const crossRate = getCrossRate(
    planning.bar,
    planning.date,
    packagePrice.packageId,
    isDayPackage,
    getExperiencesForPackage
  );

  return [
    packagePrice.packageId,
    {
      ...DEFAULT_PACKAGE_PRICE,
      ...packagePrice,
      price: crossRate,
      errors: getPackagePriceErrors(
        crossRate,
        packagePrice.discountPrice,
        isDayPackage,
        packagePrice.isForced,
        packagePrice.published,
        planning.date,
        pkg?.minimumDiscountOverrideDates ?? [],
        pkg?.minimumDiscountOverride
      ),
      discount: calculateDiscount(packagePrice.discountPrice, crossRate),
    },
  ];
};

export const completePlanning = (
  timePeriod: TimePeriod,
  room: RoomInventory,
  packages: ExtranetPackage[],
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[],
  roomCount: number,
  hasAtLeastOneDayPackage: boolean
) => {
  const allDates = eachDayOfInterval({
    start: timePeriod.startDate,
    end: timePeriod.endDate,
  });

  const existingDates = room.plannings.map((planning) => planning.date);

  const missingDates = allDates.filter(
    (date) => !existingDates.includes(formatDateWithoutHour(date))
  );

  const updatedExistingPlanning = room.plannings.map((planning) => ({
    ...DEFAULT_PLANNING,
    ...planning,
    stock: planning.stock - planning.booked,
    errors: getOpeningErrors(
      planning.bar,
      planning.stock - planning.booked,
      planning.booked,
      roomCount,
      planning.closed,
      hasAtLeastOneDayPackage
    ),
    packagePrices: {
      ...completePackage(planning.date, packages, getExperiencesForPackage),
      ...Object.fromEntries(
        planning.packagePrices.map((packagePrice) =>
          completePackagePrice(
            planning,
            packagePrice,
            packages,
            getExperiencesForPackage
          )
        )
      ),
    },
  }));

  const updatedMissingPlanning = missingDates.map((date) => {
    return {
      ...DEFAULT_PLANNING,
      date: formatDateWithoutHour(date),
      errors: getOpeningErrors(
        DEFAULT_PLANNING.bar || 0,
        DEFAULT_PLANNING.stock,
        DEFAULT_PLANNING.booked,
        roomCount,
        DEFAULT_PLANNING.closed,
        hasAtLeastOneDayPackage
      ),
      packagePrices: completePackage(
        formatDateWithoutHour(date),
        packages,
        getExperiencesForPackage
      ),
    };
  });

  const mergedPlanning = [
    ...updatedExistingPlanning,
    ...updatedMissingPlanning,
  ].sort((planningA, planningB) => {
    return isAfter(new Date(planningA.date), new Date(planningB.date)) ? 1 : -1;
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const completePlanning = mergedPlanning.reduce(
    (acc, planning) => {
      acc[planning.date] = planning;

      return acc;
    },
    {} as { [key: string]: (typeof mergedPlanning)[0] }
  );

  return { roomId: room.roomId, plannings: completePlanning };
};

const completePackage = (
  date: string,
  packages: ExtranetPackage[],
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[]
): { [key: number]: FormPackagePrice } => {
  return packages.reduce(
    (acc, pkg) => {
      const isDayPackage =
        packages.find((p) => p.id === pkg.id)?.dayPackage || false;

      const price = getCrossRate(
        DEFAULT_PLANNING.bar || 0,
        date,
        pkg.id,
        isDayPackage,
        getExperiencesForPackage
      );

      acc[pkg.id.toString()] = {
        ...DEFAULT_PACKAGE_PRICE,
        packageId: pkg.id,
        errors: getPackagePriceErrors(
          price,
          DEFAULT_PACKAGE_PRICE.discountPrice || 0,
          isDayPackage,
          DEFAULT_PACKAGE_PRICE.isForced,
          DEFAULT_PACKAGE_PRICE.published,
          date,
          pkg.minimumDiscountOverrideDates,
          pkg.minimumDiscountOverride
        ),
        price: getCrossRate(
          DEFAULT_PLANNING.bar || 0,
          date,
          pkg.id,
          isDayPackage,
          getExperiencesForPackage
        ),
        discount: 0,
      };

      return acc;
    },
    {} as { [key: string]: FormPackagePrice }
  );
};

export const completeOverviewPlanning = (
  timePeriod: TimePeriod,
  packageInventories: PackageInventory[],
  publishedPackage: ExtranetPackage,
  rooms: RoomInventory[],
  hotelInfo?: Hotel
) => {
  const allDates = eachDayOfInterval({
    start: timePeriod.startDate,
    end: timePeriod.endDate,
  });

  const mergedBookings = packageInventories.flatMap((pi) => pi.bookings);

  const formattedExistingBookings = mergedBookings.map((booking) => {
    const date = formatDateWithoutHour(new Date(booking.date));

    return {
      date,
      booked: booking.booked,
      maxBooking: booking.maxBooking,
      hasMaxBooking: booking.hasMaxBooking,
    };
  });

  const existingDates = formattedExistingBookings.map((b) => b.date);

  const missingBookings = allDates
    .map((date) => formatDateWithoutHour(date))
    .filter((date) => !existingDates.includes(date))
    .map((date) => ({ date, hasMaxBooking: false }));

  const allBookings = [...formattedExistingBookings, ...missingBookings].map(
    (value) => {
      const allRoomsStock = rooms.reduce((totalStock, room) => {
        const planning = room.plannings.find(
          // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
          (planning) => planning.date === value.date
        );

        if (planning) {
          totalStock += planning.stock || 0;
        }

        return totalStock;
      }, 0);

      const booking = mergedBookings?.find(
        // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
        (booking) => booking.date === value.date
      );
      const definedMaxBooking = booking?.hasMaxBooking
        ? booking?.maxBooking
        : publishedPackage.defaultMaxBooking;

      return {
        ...value,
        maxBooking: definedMaxBooking,
        totalStocks: allRoomsStock,
      };
    }
  );

  const bookingsByDate = allBookings.reduce<FormPackage['bookings']>(
    (acc, booking) => {
      acc[booking.date] = booking;

      return acc;
    },
    {}
  );

  return {
    packageId: publishedPackage.id,
    bookings: bookingsByDate,
  };
};

const getRoomPlanning = (
  roomIndex: string | number,
  date: string | number,
  form: FormInstance
): FormPlanning => {
  return form.getFieldValue(['rooms', roomIndex, 'plannings', date]);
};

export const handleFieldsChange = (
  changedFields: FieldData[],
  setFormHasChanged: (value: boolean) => void,
  packages: ExtranetPackage[],
  form: FormInstance,
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[],
  roomCount: number,
  hasAtLeastOneDayPackage: boolean
) => {
  if (!changedFields.length) {
    return;
  }

  setFormHasChanged(true);

  const { name, value } = changedFields[0];

  if (!Array.isArray(name)) {
    return;
  }

  if (name.length === 5 && name[0] === 'rooms') {
    const [, roomIndex, , date] = name;
    const currentPlanning = getRoomPlanning(roomIndex, date, form);
    const bar = currentPlanning?.bar || 0;
    const closed = currentPlanning?.closed || false;

    packages.forEach((pkg) => {
      const discountPrice =
        currentPlanning?.packagePrices[pkg.id]?.discountPrice || 0;
      const isForced =
        currentPlanning?.packagePrices[pkg.id]?.isForced || false;
      const published =
        currentPlanning?.packagePrices[pkg.id]?.published || false;

      const crossRate = computeCrossRate(
        pkg.id,
        getExperiencesForPackage(pkg.id),
        date as string,
        pkg.dayPackage,
        bar
      );
      const discount = calculateDiscount(discountPrice, crossRate);

      const packagePrice = currentPlanning.packagePrices[pkg.id];

      if (packagePrice) {
        packagePrice.discount = discount;
        packagePrice.price = crossRate;
        packagePrice.errors = getPackagePriceErrors(
          crossRate,
          discountPrice,
          pkg.dayPackage,
          isForced,
          published,
          date as string,
          pkg.minimumDiscountOverrideDates,
          pkg.minimumDiscountOverride
        );

        if (!isForced) {
          packagePrice.published = packagePrice.errors.length === 0;
        }
      }
    });

    currentPlanning.errors = getOpeningErrors(
      bar,
      currentPlanning.stock,
      currentPlanning.booked,
      roomCount,
      closed,
      hasAtLeastOneDayPackage
    );

    form.setFieldValue(
      ['rooms', roomIndex, 'plannings', date],
      currentPlanning
    );

    return;
  }

  if (name.length < 6 || name[0] !== 'rooms') {
    return;
  }

  const [, roomIndex, , date, , packageId, fieldType] = name;
  const currentPlanning = getRoomPlanning(roomIndex, date, form);

  if (fieldType === 'discountPrice') {
    const price = currentPlanning.packagePrices[packageId].price || 0;
    const discount = calculateDiscount(value, price);

    currentPlanning.packagePrices[packageId].discount = discount;
  }

  if (fieldType === 'discount') {
    const price = currentPlanning.packagePrices[packageId].price || 0;
    const discountPrice = Math.floor(price - (Number(value) * price) / 100);

    currentPlanning.packagePrices[packageId].discountPrice = discountPrice;
  }

  if (fieldType === 'published') {
    currentPlanning.packagePrices[packageId].isForced = true;
  }

  const isForced = currentPlanning?.packagePrices[packageId]?.isForced || false;
  const published =
    currentPlanning?.packagePrices[packageId]?.published || false;

  const pkg = packages.find((p) => p.id === parseInt(packageId as string));

  currentPlanning.packagePrices[packageId].errors = getPackagePriceErrors(
    currentPlanning.packagePrices[packageId].price || 0,
    currentPlanning.packagePrices[packageId].discountPrice || 0,
    !!packages.find((p) => p.id === parseInt(packageId as string))?.dayPackage,
    isForced,
    published,
    date as string,
    pkg?.minimumDiscountOverrideDates ?? [],
    pkg?.minimumDiscountOverride
  );

  if (!isForced) {
    currentPlanning.packagePrices[packageId].published =
      currentPlanning.packagePrices[packageId].errors.length === 0;
  }

  form.setFieldValue(['rooms', roomIndex, 'plannings', date], currentPlanning);
};

const isDiscountNotEnough = (
  discountPrice: number | undefined,
  price: number,
  isDayPackage: boolean,
  date: string,
  minimumDiscountOverrideDates: Date[],
  minimumDiscountOverride?: number
) => {
  if (!discountPrice || !price) {
    return false;
  }

  const hasOverrideForDate = minimumDiscountOverrideDates.some(
    (overrideDate) =>
      new Date(overrideDate).toDateString() === new Date(date).toDateString()
  );

  return isDayPackage
    ? 100 - (discountPrice * 100) / price < MINIMAL_DISCOUNT_DAY_PACKAGE
    : 100 - (discountPrice * 100) / price <
        (minimumDiscountOverride !== undefined &&
        minimumDiscountOverride >= 0 &&
        hasOverrideForDate
          ? minimumDiscountOverride * 100
          : MINIMAL_DISCOUNT_STAYCATION);
};

const isDiscountTooMuch = (
  discountPrice: number | undefined,
  price: number
) => {
  if (!discountPrice || !price) {
    return false;
  }

  return 100 - (discountPrice * 100) / price >= MAXIMUM_DISCOUNT;
};

export const getCellErrors = (
  errors: OpeningErrors[],
  fieldName: string,
  published: boolean,
  isForced: boolean
) => {
  if (fieldName === 'discount') {
    const fieldErrors = errors.filter(
      (e) =>
        e === OpeningErrors.NO_DISCOUNT ||
        e === OpeningErrors.EXCEED_RATE ||
        e === OpeningErrors.UNDER_MINIMUM_PRICE
    );

    return fieldErrors.length > 0
      ? { errors: fieldErrors, classNames: ['red-font'] }
      : { errors: [], classNames: [] };
  }

  if (
    fieldName === 'stock' &&
    (errors.includes(OpeningErrors.MISSING_STOCK) ||
      errors.includes(OpeningErrors.WRONG_STOCK))
  ) {
    const fieldErrors = errors.filter(
      (e) =>
        e === OpeningErrors.MISSING_STOCK || e === OpeningErrors.WRONG_STOCK
    );

    return fieldErrors.length > 0
      ? { errors: fieldErrors, classNames: ['red-font'] }
      : { errors: [], classNames: [] };
  }

  if (fieldName === 'discountPrice') {
    if (published && isForced) {
      return { errors: [], classNames: ['green-font', 'green-background'] };
    }

    const redErrors = errors.filter((e) =>
      [
        OpeningErrors.CLOSED,
        OpeningErrors.EXCEED_RATE,
        OpeningErrors.MISSING_BAR,
        OpeningErrors.MISSING_DISCOUNT_PRICE,
        OpeningErrors.NO_DISCOUNT,
        OpeningErrors.UNDER_MINIMUM_PRICE,
        OpeningErrors.WRONG_STOCK,
        OpeningErrors.REJECTED_BY_AM,
      ].includes(e)
    );

    if (errors.includes(OpeningErrors.MISSING_STOCK)) {
      return {
        errors: [OpeningErrors.MISSING_STOCK],
        classNames: ['yellow-font', 'yellow-background'],
      };
    } else if (redErrors.length > 0) {
      return { errors: errors, classNames: ['red-font', 'red-background'] };
    }

    return { errors: [], classNames: ['green-font', 'green-background'] };
  }

  if (fieldName === 'bar' && errors.includes(OpeningErrors.MISSING_BAR)) {
    return {
      errors: [OpeningErrors.MISSING_BAR],
      classNames: ['red-font'],
    };
  }

  return { errors: [], classNames: [] };
};

export const getOverviewCellErrors = (totalRemainingStock: number) => {
  const hasStock = totalRemainingStock > 0;

  if (!hasStock) {
    return ['yellow-font', 'yellow-background'];
  }

  return ['green-font', 'green-background'];
};

export const getErrorMessage = (
  error: OpeningErrors,
  stockScrapperEnabled: boolean
) => {
  if (error === OpeningErrors.CLOSED && stockScrapperEnabled) {
    return 'You have applied a stop sell on this day';
  }

  return OpeningsErrorMessage[error];
};
